import { createContext, useContext, useEffect } from "react";
import type { ChatArtifact, ChatArtifactSurface, ChatArtifactView } from "./kit";

/** Only a mounted, authorized owner can supply the contents of a remembered tab. */
export const ConversationArtifactRegistration = createContext<
  ((view: ChatArtifactView) => () => void) | undefined
>(undefined);

/** Show what the agent is working on: open, or bring forward, the artifact a turn in
 *  progress made or changed, unless the person keeps another tab in view. */
export const ConversationArtifactFollow = createContext<
  ((view: ChatArtifactView) => void) | undefined
>(undefined);

/** True inside the turn the agent is still writing. */
export const ConversationTurnLive = createContext(false);

/** Offer an owner's view to the conversation's workspace. Inside a turn in progress, each
 *  new view (a new artifact, or a new version of one) is also shown as it arrives. */
export function useAvailableConversationArtifact(
  view: ChatArtifactView | undefined,
) {
  const register = useContext(ConversationArtifactRegistration);
  const follow = useContext(ConversationArtifactFollow);
  const live = useContext(ConversationTurnLive);
  useEffect(() => {
    if (view && register) return register(view);
  }, [register, view]);
  useEffect(() => {
    if (view && live && follow) follow(view);
  }, [follow, live, view]);
}

/** Open a document from a nested transcript in the same conversation workspace. */
export const ConversationArtifactDocumentOpen = createContext<
  ((artifact: ChatArtifact) => void) | undefined
>(undefined);

/** Open an owner's surface in the conversation's workspace, from anywhere in its thread. */
export const ConversationArtifactOpen = createContext<
  ((surface: ChatArtifactSurface) => void) | undefined
>(undefined);

export function useOpenConversationArtifact() {
  return useContext(ConversationArtifactOpen);
}

/** The other readings an artifact offers beside itself: its source, and its results. */
export const ARTIFACT_PANES = ["source", "results"] as const;
export type ArtifactPane = (typeof ARTIFACT_PANES)[number];

export function artifactPaneOf(value: unknown): ArtifactPane | undefined {
  return ARTIFACT_PANES.find((pane) => pane === value);
}

/** Where a page keeps the open artifact in its address, so a reload or a shared link opens
 *  it again: the artifact's id, and which of its readings is shown. */
export interface ConversationArtifactAddress {
  readonly artifact: string | undefined;
  readonly pane: ArtifactPane | undefined;
  /** `step` when a person opened or closed it, so Back undoes it; following the agent to an
   *  artifact it opened, or changing a pane, replaces the address instead. */
  readonly onAddress: (
    next: {
      readonly artifact: string | undefined;
      readonly pane: ArtifactPane | undefined;
    },
    step?: boolean,
  ) => void;
  /** The tab kept in view while the agent opens others (`?pinned=`), when the page keeps
   *  it in its address; else the browser remembers it. */
  readonly pinned?: string;
  readonly onPin?: (pinned: string | undefined) => void;
}

export const ConversationArtifactAddressContext = createContext<
  ConversationArtifactAddress | undefined
>(undefined);

export function useConversationArtifactAddress() {
  return useContext(ConversationArtifactAddressContext);
}

/** One version of an artifact the agent keeps in a conversation: its body, and when it
 *  was made. */
export interface ConversationArtifactRead {
  readonly body: string;
  readonly at: Date | undefined;
}

export type ConversationArtifactReader = (
  artifactId: string,
  version: number,
  signal?: AbortSignal,
) => Promise<ConversationArtifactRead>;

/** Read one version of an artifact the agent keeps in this conversation. */
export const ConversationArtifactBody = createContext<ConversationArtifactReader | undefined>(
  undefined,
);

/** Put words in the conversation's composer for the person to finish and send: an
 *  artifact's next step that is the person's to ask for (Add part). */
export const ConversationComposerDraft = createContext<((text: string) => void) | undefined>(
  undefined,
);
